import React, { useEffect, useState, useContext, useMemo } from 'react';
import PlayerProfileModal from '../components/PlayerProfileModal';
import { auth, db } from "../lib/firebase";
import { updateProfile } from "firebase/auth";
import Layout from "../central/Layout";
import Panel from "../central/Panel";
import Button from '../central/Button';
import { HeadingSmall, BodyText } from "../central/Typography";
import theme from '../central/theme';
import { getDocs, doc, getDoc, updateDoc, query } from "firebase/firestore";
import styled from 'styled-components';
import { useNavigate } from "react-router-dom";
import { LeagueContext } from "../context/LeagueContext";
import { paths } from "../lib/paths";
import { setActiveSeasonID } from "../lib/seasonPaths";
import { useToast } from "../context/ToastContext";

const LeagueCard = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 20px 22px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  min-height: 72px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;

const Profile = () => {
  const [user, setUser] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [saving, setSaving] = useState(false);
  const [currentLeagueID, setCurrentLeagueIDState] = useState("");
  const [currentLeagueName, setCurrentLeagueName] = useState("");
  const [currentRole, setCurrentRole] = useState("user");
  const [linkedPlayerID, setLinkedPlayerID] = useState("");
  const [notificationPermission, setNotificationPermission] = useState(null);
  const [linkedPlayerSaving, setLinkedPlayerSaving] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [leaguePlayers, setLeaguePlayers] = useState([]);
  const navigate = useNavigate();
  const { setCurrentLeagueId } = useContext(LeagueContext);
  const { showToast } = useToast();

  const loadSeasonLeaguePlayers = async (leagueId) => {
    const leagueSnap = await getDoc(paths.league(leagueId));
    if (!leagueSnap.exists()) {
      return [];
    }
    const leagueData = leagueSnap.data() || {};
    const resolvedSeasonID =
      typeof leagueData.currentSeasonID === "string" && leagueData.currentSeasonID.trim() !== ""
        ? leagueData.currentSeasonID.trim()
        : "S1";
    setActiveSeasonID(resolvedSeasonID);
    const playersRef = paths.leaguePlayers(leagueId);
    const snapshot = await getDocs(query(playersRef));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  const handleSelectLeague = async (leagueId) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, { currentLeagueID: leagueId });

      if (typeof setCurrentLeagueId === "function") {
        setCurrentLeagueId(leagueId);
      }

      const selectedLeague = leagues.find((league) => league.id === leagueId);
      setCurrentLeagueIDState(leagueId);
      setCurrentLeagueName(selectedLeague?.name || "");
      setCurrentRole((user?.roles && user.roles[leagueId]) || "user");

      const playersData = await loadSeasonLeaguePlayers(leagueId);

      const sortedPlayers = [...playersData].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      );

      setLeaguePlayers(sortedPlayers);

      const currentUserRef = await getDoc(userRef);
      const refreshedUserData = currentUserRef.exists() ? currentUserRef.data() : {};
      const nextLinkedPlayerID =
        (refreshedUserData.linkedPlayerIDs && refreshedUserData.linkedPlayerIDs[leagueId]) || "";

      setLinkedPlayerID(nextLinkedPlayerID);
    } catch (err) {
      console.error("Error selecting league:", err);
      showToast("Failed to switch league. Please try again.", "error");
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        try {
          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            const nextCurrentLeagueID = userData.currentLeagueID || "";
            const nextRole = (userData.roles && nextCurrentLeagueID && userData.roles[nextCurrentLeagueID]) || "user";
            const nextLinkedPlayerID = (userData.linkedPlayerIDs && nextCurrentLeagueID && userData.linkedPlayerIDs[nextCurrentLeagueID]) || "";

            setUser({
              ...currentUser,
              displayName: userData.displayName || currentUser.displayName,
              email: userData.email || currentUser.email,
              roles: userData.roles || {},
            });
            setEditedName(userData.displayName || currentUser.displayName || "");

            setCurrentLeagueIDState(nextCurrentLeagueID);
            setCurrentRole(nextRole);
            setLinkedPlayerID(nextLinkedPlayerID);
            setNotificationPermission(userData.notificationPermission || null);

            const leagueIDs = userData.leagueIDs || [];

            const leaguesData = [];
            for (const id of leagueIDs) {
              const leagueRef = doc(db, "leagues", id);
              const leagueSnap = await getDoc(leagueRef);
              if (leagueSnap.exists()) {
                leaguesData.push({ id, ...leagueSnap.data() });
              }
            }

            setLeagues(leaguesData);

            if (nextCurrentLeagueID) {
              const activeLeague = leaguesData.find((league) => league.id === nextCurrentLeagueID);
              setCurrentLeagueName(activeLeague?.name || "");

              const playersData = await loadSeasonLeaguePlayers(nextCurrentLeagueID);

              const sortedPlayers = [...playersData].sort((a, b) =>
                String(a.name || "").localeCompare(String(b.name || ""))
              );

              setLeaguePlayers(sortedPlayers);
            } else {
              setCurrentLeagueName("");
              setLeaguePlayers([]);
            }
          } else {
            setUser(currentUser);
            setLeagues([]);
            setCurrentLeagueIDState("");
            setCurrentLeagueName("");
            setCurrentRole("user");
            setLinkedPlayerID("");
            setLeaguePlayers([]);
          }
        } catch (error) {
          console.error("Error fetching leagues:", error);
          setUser(currentUser);
          setLeagues([]);
          setCurrentLeagueIDState("");
          setCurrentLeagueName("");
          setCurrentRole("user");
          setLinkedPlayerID("");
          setLeaguePlayers([]);
        }
      } else {
        setUser(null);
        setLeagues([]);
        setCurrentLeagueIDState("");
        setCurrentLeagueName("");
        setCurrentRole("user");
        setLinkedPlayerID("");
        setLeaguePlayers([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSignOut = () => {
    auth.signOut();
  };

  const trimmedEditedName = useMemo(() => editedName.trim(), [editedName]);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      if (!trimmedEditedName) return;

      await updateProfile(currentUser, {
        displayName: trimmedEditedName,
      });

      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, { displayName: trimmedEditedName });

      setUser((prev) => ({ ...prev, displayName: trimmedEditedName }));
      setEditedName(trimmedEditedName);
      setIsEditing(false);
      showToast("Profile saved!", "success");
    } catch (err) {
      console.error("Error updating profile:", err);
      showToast("Failed to save profile. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLinkedPlayer = async () => {
    try {
      setLinkedPlayerSaving(true);
      const currentUser = auth.currentUser;
      if (!currentUser || !currentLeagueID) return;

      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        [`linkedPlayerIDs.${currentLeagueID}`]: linkedPlayerID || "",
      });

      showToast("Player linked successfully!", "success");
    } catch (err) {
      console.error("Error linking player:", err);
      showToast("Failed to save player link. Please try again.", "error");
    } finally {
      setLinkedPlayerSaving(false);
    }
  };

  const linkedPlayer = leaguePlayers.find((player) => player.id === linkedPlayerID) || null;

  const editLinkStyle = {
    color: "#888",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: 600,
    lineHeight: 1,
  };

  const infoSectionStyle = {
    background: "#fafafa",
    border: "1px solid #ececec",
    borderRadius: "12px",
    padding: "16px 18px",
    marginBottom: theme.spacing.large,
  };

  const activeLeagueHelperText = currentLeagueID
    ? "Your details and stats are shown below for this league."
    : "Select a league below to view league-specific details.";

  const sectionLabelStyle = {
    fontWeight: "bold",
    marginBottom: "0.35rem",
  };

  const subtleHelperStyle = {
    color: "#666",
    fontSize: "0.9rem",
    lineHeight: 1.45,
    marginBottom: theme.spacing.small,
  };

  const activeBadgeStyle = {
    background: "rgba(255,255,255,0.2)",
    border: "1px solid rgba(255,255,255,0.35)",
    borderRadius: "999px",
    padding: "4px 10px",
    fontSize: "0.75rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
  };

  return (
    <Layout>
      <Panel style={{ backgroundColor: theme.colors.surface, padding: theme.spacing.medium }}>
        <HeadingSmall style={{ marginBottom: theme.spacing.large, fontWeight: "bold" }}>My Profile</HeadingSmall>
        {!user ? (
          <BodyText >Please sign in to view your profile.</BodyText>
        ) : (
          <>
            <div style={infoSectionStyle}>
            <HeadingSmall style={{ marginBottom: theme.spacing.small, fontWeight: "bold" }}>
              Account
              {notificationPermission !== "granted" && (
                <span style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: "#E53935", display: "inline-block",
                  marginLeft: 6, verticalAlign: "middle"
                }} />
              )}
            </HeadingSmall>

              <div style={{ marginBottom: theme.spacing.small }}>
                <div style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>Your Name</div>

                {!isEditing ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <BodyText style={{ marginBottom: 0 }}>
                      {user.displayName || 'N/A'}
                    </BodyText>

                    <span
                      style={editLinkStyle}
                      onClick={() => {
                        setEditedName(user?.displayName || "");
                        setIsEditing(true);
                      }}
                    >
                      Edit
                    </span>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '0.75rem', maxWidth: '420px' }}>
                    <input
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      style={{
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        width: '100%',
                      }}
                      placeholder="Enter your name"
                    />

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button onClick={handleSaveProfile} disabled={saving || !trimmedEditedName} variant="pink">
                        {saving ? "Saving..." : "Save"}
                      </Button>

                      <Button
                        onClick={() => {
                          setEditedName(user?.displayName || "");
                          setIsEditing(false);
                        }}
                        variant="blue"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

              </div>

              <div>
                <div style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>Email</div>
                <BodyText style={{ marginBottom: 0 }}>
                  {user.email || 'N/A'}
                </BodyText>
              </div>
            </div>

              <div style={infoSectionStyle}>
              <HeadingSmall style={{ marginBottom: theme.spacing.small, fontWeight: "bold" }}>
                Active League
              </HeadingSmall>
              <div style={{ marginBottom: theme.spacing.small }}>
                <BodyText style={{ marginBottom: "0.35rem", fontWeight: 700 }}>
                  {currentLeagueName || "No active league selected"}
                </BodyText>
                <div style={subtleHelperStyle}>{activeLeagueHelperText}</div>
              </div>

              <div style={{ marginBottom: theme.spacing.medium }}>
                <div style={sectionLabelStyle}>Role</div>
                <BodyText style={{ marginBottom: 0, textTransform: "capitalize" }}>
                  {currentRole || "user"}
                </BodyText>
              </div>

              <div style={{ marginBottom: theme.spacing.medium }}>
                <div style={sectionLabelStyle}>
                  Claim Player
                  {!linkedPlayerID && currentLeagueID && (
                    <span style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: "#E53935", display: "inline-block",
                      marginLeft: 6, verticalAlign: "middle", flexShrink: 0
                    }} />
                  )}
                </div>
                {currentLeagueID ? (
                  <div style={{ display: "grid", gap: "0.75rem", maxWidth: "420px" }}>
                <select
                  value={linkedPlayerID}
                  onChange={(e) => {
                    setLinkedPlayerID(e.target.value);
                  }}
                  style={{
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    width: "100%",
                    boxSizing: "border-box",
                    background: "white",
                  }}
                >
                  <option value="">Claim your player</option>
                  {leaguePlayers.map((player) => (
                    <option key={player.id} value={player.id}>
                      {player.name}
                    </option>
                  ))}
                </select>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                      <Button onClick={handleSaveLinkedPlayer} disabled={linkedPlayerSaving || !currentLeagueID} variant="pink" style={{ minWidth: "120px" }}>
                        {linkedPlayerSaving ? "Saving..." : "Save Player"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <BodyText style={{ marginBottom: 0 }}>
                    Select a league below to link yourself to a player.
                  </BodyText>
                )}
              </div>

              {linkedPlayer ? (
                <div style={{ marginBottom: 0 }}>
                  <Button
                    onClick={() => setProfileModalOpen(true)}
                    variant="blue"
                    style={{ minWidth: "120px" }}
                  >
                    View My Stats
                  </Button>
                </div>
              ) : null}
            </div>

            {leagues.length > 0 ? (
              <>
                <HeadingSmall style={{ marginTop: theme.spacing.large, marginBottom: "0.35rem" ,fontWeight: "bold"}}>Your Leagues</HeadingSmall>
                <div style={{ color: "#666", fontSize: "0.9rem", marginBottom: theme.spacing.medium }}>
                  Choose which league to view in your profile.
                </div>
                {leagues.map((league) => (
                  <LeagueCard
                    key={league.id}
                    onClick={() => handleSelectLeague(league.id)}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: "0.75rem" }}>
                      <BodyText style={{ marginBottom: 0, color: 'white', fontSize: '1.05rem', fontWeight: 600 }}>
                        {league.name}
                      </BodyText>
                      {league.id === currentLeagueID ? (
                        <span style={activeBadgeStyle}>Active</span>
                      ) : null}
                    </div>
                  </LeagueCard>
                ))}
              </>
            ) : (
              <BodyText style={{ marginTop: theme.spacing.large }}>You are not part of any leagues.</BodyText>
            )}
            <Button
              variant="red"
              onClick={handleSignOut}
              style={{ marginTop: theme.spacing.large }}
            >
              Sign Out
            </Button>
          </>
        )}
      </Panel>
      <PlayerProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        leagueID={currentLeagueID}
        playerID={linkedPlayerID}
        player={linkedPlayer}
      />
    </Layout>
  );
};

export default Profile;
